import { Trophy, ArrowRight, Medal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cn, formatScore, scoreColor } from '@/lib/utils';
import type { HomeLeaderboardQuery } from '@/gql/graphql';

type LeaderboardData = NonNullable<HomeLeaderboardQuery['globalLeaderboard']>;
type LeaderboardEntry = NonNullable<LeaderboardData['items']>[number];

interface LeaderboardWidgetProps {
    leaderboard: LeaderboardData;
}

const PODIUM_COLORS = [
    { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', medal: 'text-amber-500' },
    { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', medal: 'text-slate-400' },
    { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', medal: 'text-orange-400' },
] as const;

export function LeaderboardWidget({ leaderboard }: LeaderboardWidgetProps) {
    const { t } = useTranslation('home');

    const items = leaderboard.items ?? [];

    if (items.length === 0) {
        return (
            <div className="space-y-4">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Trophy size={20} className="text-emerald-500" />
                    {t('sections.leaderboard')}
                </h2>
                <div className="py-10 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                    <Trophy size={32} className="mx-auto mb-2 text-slate-200" />
                    <p className="text-slate-400 text-sm font-medium">{t('leaderboard.empty')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Trophy size={20} className="text-emerald-500" />
                    {t('sections.leaderboard')}
                </h2>
                <Link
                    to="/ranking"
                    className="text-sm font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                >
                    {t('leaderboard.fullRanking')}
                    <ArrowRight size={14} />
                </Link>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {/* Top 3 entries */}
                <div className="divide-y divide-slate-100">
                    {items.map((entry, index) => (
                        <PodiumEntry key={entry.user?.id} entry={entry} position={index + 1} />
                    ))}
                </div>

                {/* Current user position */}
                {leaderboard.currentUserRank != null && leaderboard.currentUserRank > 3 && (
                    <>
                        <div className="border-t border-dashed border-slate-200 mx-4" />
                        <div className="px-4 py-3 flex items-center gap-3 bg-emerald-50/50">
                            <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-black flex items-center justify-center flex-shrink-0">
                                {leaderboard.currentUserRank}
                            </span>
                            <span className="text-sm font-bold text-emerald-700 flex-1">
                                {t('leaderboard.you')}
                            </span>
                            <span className={cn("text-sm font-black", scoreColor(leaderboard.currentUserScore ?? 0, "text-emerald-700"))}>
                                {formatScore(leaderboard.currentUserScore ?? 0)} pts
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function PodiumEntry({ entry, position }: { entry: LeaderboardEntry; position: number }) {
    const { t } = useTranslation('home');
    const colors = PODIUM_COLORS[position - 1] ?? PODIUM_COLORS[2];
    const user = entry.user;

    return (
        <div className="px-4 py-3 flex items-center gap-3">
            {/* Position medal */}
            <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
                colors.bg
            )}>
                <Medal size={14} className={colors.medal} />
            </div>

            {/* Avatar */}
            {user?.pictureUrl ? (
                <img
                    src={user.pictureUrl}
                    alt={user.username ?? ''}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
            ) : (
                <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {(user?.username ?? '?')[0].toUpperCase()}
                </div>
            )}

            {/* Username + race count */}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{user?.username}</p>
                <p className="text-xs text-slate-400">
                    {t('leaderboard.races', { count: entry.raceCount ?? 0 })}
                </p>
            </div>

            {/* Score */}
            <span className={cn("text-sm font-black", colors.text)}>
                {formatScore(entry.totalScore ?? 0)} pts
            </span>
        </div>
    );
}
